import {useWindowWidth} from "../../../../../services/useWindowWidth";
import React from "react";

import styles from './ComparisonSection.module.scss';
import NumberedListCard from "../../components/numbered-list-card/NumberedListCard";
import OutlinedButton from "../../components/buttons/Outlined";
import ContainedButton from "../../components/buttons/Contained";
import {EMOJIS} from "../../../../../config/constants";
import type {PartnerVariant, ResultListItemData} from "../../types";
import H1 from "../../components/typography/H1";
import H4 from "../../components/typography/H4";
import {getPartnerLanguage} from '../../locales/language';
import certCentersRu from '../../locales/certCentersRu';

const negativeList = [
    'Incomplete shipment details mean extra time spent clarifying incoming requests',
    'Working hours go into preliminary quotes that never turn into orders',
    'Without automation, preparing quotes and updating rates takes too long'
];

const negativeListResult: ResultListItemData[] = [
    {
        emoji: EMOJIS.FACE_WITH_STEAM_FROM_NOSE,
        text: 'Where is the invoice?',
    },
    {
        emoji: EMOJIS.ANGRY_FACE,
        text: 'Thanks, but too pricey!',
    },
    {
        emoji: EMOJIS.EXPLODING_HEAD,
        text: 'Is my quote ready yet?',
    },
]

const positiveList = [
    'Complete shipment details and a verified set of documents',
    'Requests come with an approved budget: clients calculate the costs themselves',
    'Rates load automatically. Calculations take around seven minutes on average'
];

const positiveListResult: ResultListItemData[] = [
    {
        emoji: EMOJIS.PARTYING_FACE,
        text: "Yes, it’s all here!",
    },
    {
        emoji: EMOJIS.STAR_STRUCK,
        text: "All set. Let’s go!",
    },
    {
        emoji: EMOJIS.SMILING_FACE_WITH_SUNGLASSES,
        text: "Wow! That was fast!",
    },
]

const mobilePositiveList = [positiveList[0], positiveList[2], positiveList[1]];
const russianPositiveListResult: ResultListItemData[] = [
    {
        emoji: EMOJIS.PARTYING_FACE,
        text: <span className={styles.confirmationCaption}>Yes,<br/>it’s all here!</span>,
    },
    {
        emoji: EMOJIS.STAR_STRUCK,
        text: 'All set. Let’s go!',
    },
    {
        emoji: EMOJIS.SMILING_FACE_WITH_SUNGLASSES,
        text: <>Wow! <span className={styles.quickResultLine}>That was fast!</span></>,
    },
];

type ComparisonSectionProps = {
    variant?: PartnerVariant;
};

function ComparisonSection({variant = 'transport'}: ComparisonSectionProps) {
    const isCertCenter = variant === 'cert-centers';
    const transportResults = getPartnerLanguage() === 'en' ? positiveListResult : russianPositiveListResult;
    const mobilePositiveListResult = [transportResults[0], transportResults[2], transportResults[1]];

    const windowWidth = useWindowWidth();
    const isMobile = windowWidth <= 575;
    const isSmallScreen = windowWidth >= 576 && windowWidth <= 767;
    const isMediumScreen = windowWidth >= 768;
    const useCompactLayout = isMobile || isSmallScreen || isMediumScreen;

    const negativeItems = isCertCenter ? certCentersRu.comparison.negativeList : negativeList;
    const negativeResults = isCertCenter ? certCentersRu.comparison.negativeResults : negativeListResult;

    let positiveItems = useCompactLayout ? mobilePositiveList : positiveList;
    let results = useCompactLayout ? mobilePositiveListResult : transportResults;

    if (isCertCenter) {
        positiveItems = certCentersRu.comparison.positiveList;
        results = certCentersRu.comparison.positiveResults;
    }

    return (
        <div className={styles.section} id="features">
            <div className={styles.sectionContainer}>
                <div className={styles.titleContainer}>
                    <H1>Make client work easier</H1>
                </div>
                <div className={styles.cardsBlock}>
                    <NumberedListCard
                        title='The usual process'
                        list={negativeItems}
                        isError
                        listResult={negativeResults}
                        subtitle='This leads to:'
                    />
                    <NumberedListCard
                        title='The Vedmarket solution'
                        list={positiveItems}
                        listResult={results}
                        subtitle='The result:'
                    />
                </div>
            </div>
            <div className={styles.actionsContainer}>
                <div className={styles.actionsBlock}>
                    <div className={styles.actionsTitle}>
                        <span className={styles.actionsEmoji}>{EMOJIS.SPARKLES}</span>
                        <H4 sx={{
                            boxSizing: 'border-box',
                            paddingTop: 0,
                            '@media (min-width: 1400px)': {
                                textWrap: 'balance',
                            },
                            width: '100%',
                            '@media (max-width: 575px)': {
                                textWrap: 'wrap',
                            },
                            '@media (min-width: 768px)': {
                                position: 'relative',
                                top: '4px',
                            },
                            '@media screen and (min-width: 576px) and (max-width: 767px)': {
                                position: 'relative',
                                top: '3px',
                            },
                        }}>
                            Get started today
                        </H4>
                    </div>
                    <div className={styles.actionsButtons}>
                        <ContainedButton icon={windowWidth <= 767 ? null : 'handshake'}>Become a partner</ContainedButton>
                        <OutlinedButton icon={windowWidth <= 767 ? null : 'play'}>Watch demo</OutlinedButton>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default ComparisonSection
